import { PATHS } from 'router/config';

import { marketingDataSubTabOfQuality } from './marketingModel';

/**
 * ТЗ 0103 — старые адреса раздела «Маркетинг» (до новой структуры вкладок,
 * design-erp §1.1). Они вшиты в задачи сервера (`signal_tasks.signal_path`
 * писал `tab=pulse|channels|creatives|search`), в закладки и в ссылки
 * коллегам — поэтому это редирект навсегда. Страница зовёт
 * `marketingLegacyTabTarget` до любого запроса (как
 * `marketingLegacySettingsTarget`), остальные параметры адреса
 * (`period/from/to/ch/fam/offer/le/model/camp/insp/…`) переносятся как есть.
 *
 * | Было                                         | Стало                                  |
 * |----------------------------------------------|----------------------------------------|
 * | `tab=pulse`                                  | `tab=leads`                            |
 * | `tab=channels` (`dim`, `path`)               | `tab=ads` (те же `dim`, `path`)        |
 * | `tab=creatives` (`fatigue`, `fmt`, `ad=`)    | `tab=ads&dim=AD`, `ad=` → `insp=ad:`   |
 * | `tab=creatives&ctab=formats`                 | `tab=ads&ctab=matrix`                  |
 * | `tab=search` без `stab` / `stab=google`      | `tab=seo&stab=engines`                 |
 * | `tab=search&stab=bing`                       | `tab=seo&stab=engines&eng=bing`        |
 * | `tab=search&stab=site`                       | `tab=journey&jtab=pages`               |
 * | `tab=search&stab=ai` / `demand`              | `tab=seo&stab=ai` / `demand`           |
 * | `tab=search&stab=terms`                      | `tab=ads&ctab=terms`                   |
 * | `tab=journey&jtab=cohorts`                   | `tab=leads&ltab=cohorts`               |
 * | `tab=quality` (`qtab=summary`, `ch`, окно)   | `tab=data&dtab=quality` (`ch`, окно)   |
 * | `tab=quality&qtab=mapping` / `sales`         | `tab=data&dtab=mapping` / `sales`      |
 * | `tab=seo&stab=links` (`insp`)                | `tab=seo&stab=competitors#seo-links`   |
 *
 * Повторное ревью 0103, пакет 2 (P4) — служебных вкладок меньше:
 * «Качество данных» стало подвкладками «Данных» (одним `dtab`); сервер
 * по-прежнему пишет `tab=quality&ch=…` в задачи сигналов «метки не
 * дошли», «кампании не сопоставлены», «покрытие продаж упало» и гипотезы
 * «починить разметку» — канал и окно переносятся. SEO «Ссылки» — раздел
 * «Конкурентов» (`#seo-links` раскрывает его); так пишет и сервер у
 * возможности `link_gap`.
 *
 * Не старый адрес — `null` (новые адреса сервера после ТЗ 0103 тоже `null`).
 */

/** Якорь раздела «Ссылки» внутри SEO › «Конкуренты». */
export const MARKETING_SEO_LINKS_ANCHOR = 'seo-links';

/** Старый адрес выбранного объявления «Креативов»: `ad=295`. */
const LEGACY_AD = 'ad';

/** Мост `stab` «Поиска и сайта» → новое место. */
const SEARCH_TARGETS: Readonly<
  Record<string, Readonly<Record<string, string | null>>>
> = {
  google: { tab: 'seo', stab: 'engines' },
  bing: { tab: 'seo', stab: 'engines', eng: 'bing' },
  site: { tab: 'journey', jtab: 'pages', stab: null },
  ai: { tab: 'seo', stab: 'ai' },
  demand: { tab: 'seo', stab: 'demand' },
  terms: { tab: 'ads', ctab: 'terms', stab: null },
};

const apply = (
  params: URLSearchParams,
  patch: Readonly<Record<string, string | null>>,
) => {
  Object.entries(patch).forEach(([name, value]) => {
    if (value === null) params.delete(name);
    else params.set(name, value);
  });
};

/** Новое место старого адреса; `null` — адрес не старый. */
export const marketingLegacyTabTarget = (search: string): string | null => {
  const params = new URLSearchParams(search);
  const tab = params.get('tab');
  switch (tab) {
    case 'pulse':
      params.set('tab', 'leads');
      break;
    case 'channels':
      params.set('tab', 'ads');
      // Подвкладок у «Каналов» не было — чужой `ctab` не уводит в карту.
      params.delete('ctab');
      break;
    case 'creatives': {
      const formats = params.get('ctab') === 'formats';
      params.set('tab', 'ads');
      params.delete('path');
      if (formats) {
        params.set('ctab', 'matrix');
        params.delete('dim');
      } else {
        params.delete('ctab');
        params.set('dim', 'AD');
      }
      const ad = (params.get(LEGACY_AD) ?? '').trim();
      params.delete(LEGACY_AD);
      if (/^\d+$/.test(ad) && !params.get('insp')) {
        params.set('insp', `ad:${ad}`);
      }
      break;
    }
    case 'search': {
      const stab = (params.get('stab') ?? '').trim();
      apply(params, SEARCH_TARGETS[stab] ?? SEARCH_TARGETS.google);
      break;
    }
    case 'journey':
      if (params.get('jtab') !== 'cohorts') return null;
      params.set('tab', 'leads');
      params.delete('jtab');
      params.set('ltab', 'cohorts');
      break;
    case 'quality': {
      const qtab = (params.get('qtab') ?? '').trim();
      params.set('tab', 'data');
      params.delete('qtab');
      params.set('dtab', marketingDataSubTabOfQuality(qtab));
      break;
    }
    case 'seo':
      if (params.get('stab') !== 'links') return null;
      params.set('stab', 'competitors');
      return `${
        PATHS.marketing
      }?${params.toString()}#${MARKETING_SEO_LINKS_ANCHOR}`;
    default:
      return null;
  }
  return `${PATHS.marketing}?${params.toString()}`;
};
